import { StorageError } from './errors.js'
import type { RevisionCondition, StoredRecord, VaultRecordKind } from './types.js'

/**
 * Low-level persistence ports. Implementations must be atomic: the revision
 * check and the write happen in one transaction so concurrent tabs produce
 * exactly one winner (LOC-03).
 */
export interface RecordStore {
  get(key: string): Promise<StoredRecord | null>
  put(record: StoredRecord, condition: RevisionCondition): Promise<void>
  delete(key: string, condition: RevisionCondition): Promise<void>
  /** Every record of the kind, unordered. Callers that need the vault's list order sort it. */
  list(kind?: VaultRecordKind): Promise<StoredRecord[]>
  /** How many records the store holds, without reading any of them. */
  count(kind?: VaultRecordKind): Promise<number>
  /**
   * A bounded slice of one kind **in the vault's list order** (`compareRecordsForListing`):
   * `offset` is a seek, not a read, so a surface that shows twenty of ten thousand records
   * pays for twenty. The order is the same one `list` is sorted into, by construction.
   */
  query(query: RecordQuery): Promise<StoredRecord[]>
  /** Content-free completion/document lookup; reads only the newest matching record. */
  latestCompletionForDocument(documentId: string): Promise<StoredRecord | null>
}

/** A bounded read over one record kind, newest commit first. */
export interface RecordQuery {
  readonly kind: VaultRecordKind
  /** How many records to return. Omitted means every record of the kind. */
  readonly limit?: number
  /** How many records of the kind to skip before collecting. Defaults to 0. */
  readonly offset?: number
}

/**
 * Immutable blob port. `put` is create-only: an existing key is a conflict,
 * which keeps source bytes immutable and lets commit recovery select the last
 * complete revision instead of mixing old records with new bytes (LOC-02).
 */
export interface BlobStore {
  put(key: string, data: Uint8Array): Promise<void>
  get(key: string): Promise<Uint8Array | null>
  has(key: string): Promise<boolean>
  delete(key: string): Promise<void>
  list(): Promise<string[]>
}

export function assertCondition(
  condition: RevisionCondition,
  current: StoredRecord | null,
  key: string,
): void {
  if (condition.kind === 'any') return
  if (condition.kind === 'must_not_exist') {
    if (current !== null) {
      throw new StorageError('OP_STORAGE_REVISION_CONFLICT', `${key} already exists`, {
        current_revision: current.revision,
      })
    }
    return
  }
  if (current === null) {
    throw new StorageError('OP_STORAGE_REVISION_CONFLICT', `${key} no longer exists`)
  }
  if (current.revision !== condition.revision) {
    throw new StorageError(
      'OP_STORAGE_REVISION_CONFLICT',
      `${key} is at revision ${current.revision}, expected ${condition.revision}`,
      { current_revision: current.revision },
    )
  }
}
